import { Box, Editor, TLShape, getDefaultColorTheme } from "tldraw";
import { ET_TYPESET_SHAPE_TYPES } from "@ever-tutor/et-ui";
import {
  TypesetLayerCache,
  TypesetLayerPlan,
  isTopLevelTypeset,
  typesetLayerBlit,
  typesetLayerCanvasSize,
} from "./typesetLayerCache";
import { isStudentInk } from "./studentInkClusters";
import { screenshotJpegEncoder } from "./screenshotJpegEncoder";
import { renderWithRetries } from "../utils/renderWithRetries";
import { renderContentWithRetries } from "../utils/renderContentWithRetries";

/** Student pens have no black; the model reads handwriting better without hue. */
export const RASTERIZED_INK_COLOR = "#000000";

/** Pinned light: black ink on tldraw's near-black dark sheet is invisible. */
const EXPORT_THEME = getDefaultColorTheme({ isDarkMode: false });

/** Caller must `close()` — an ImageBitmap holds native memory past GC. */
async function decodeBlob(blob: Blob): Promise<ImageBitmap> {
  return createImageBitmap(blob);
}

/** JPEG-encode a canvas to a data URL, off the main thread where possible. */
export async function canvasToDataUrl(
  canvas: HTMLCanvasElement,
): Promise<string> {
  return screenshotJpegEncoder.encode(canvas);
}

/** A canvas of exactly `w`x`h` holding `image` at its origin; blank for null. */
function imageToCanvas(
  image: CanvasImageSource | null,
  w: number,
  h: number,
): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  if (image) ctx.drawImage(image, 0, 0);
  return { canvas, ctx };
}

/** `source-in` keeps the layer's alpha and takes our colour, so the taper survives. */
function paintBlack(
  image: CanvasImageSource,
  w: number,
  h: number,
): HTMLCanvasElement | null {
  const layer = imageToCanvas(image, w, h);
  if (!layer) return null;
  layer.ctx.globalCompositeOperation = "source-in";
  layer.ctx.fillStyle = RASTERIZED_INK_COLOR;
  layer.ctx.fillRect(0, 0, w, h);
  return layer.canvas;
}

/** Renders one batch of shapes, or returns null if that render failed. */
export type RenderBatch = (
  shapes: TLShape[],
  opts: Parameters<Editor["toImage"]>[1],
) => Promise<{ blob: Blob } | null>;

export interface TypesetRenderResult {
  /** The shapes it did not draw. */
  handed_back: TLShape[];
  /** False when what it drew is missing a face: shown, never cached. */
  is_complete: boolean;
}

/** Draws typeset shapes straight onto a canvas that covers `bounds`. */
export type RenderTypeset = (
  shapes: TLShape[],
  bounds: Box,
  ctx: CanvasRenderingContext2D,
) => Promise<TypesetRenderResult>;

export interface BlackInkRasterOptions {
  bounds: Box;
  scale: number;
  outputW: number;
  outputH: number;
  /** Shapes per render. Defaults to no limit, i.e. one render per layer. */
  batchSize?: number;
  renderBatch: RenderBatch;
  /** Awaited between renders, so a long board does not hold the main thread. */
  onBatchBoundary?: () => Promise<void>;
  /**
   * Draws the shapes it names from one reusable bitmap instead of exporting
   * them. Its shapes are removed from every live pass, so passing a shape here
   * and in `shapes` paints it once, not twice.
   */
  typesetLayer?: TypesetLayerCache;
  /**
   * Draws shapes into the typeset layer when the cache rebuilds or patches
   * it, instead of exporting them through `renderBatch`.
   */
  renderTypeset?: RenderTypeset;
  /**
   * Cluster key for an ink shape. A render that throws takes out everything in
   * the call, so this is the unit a failure is isolated to — a cluster missing
   * some of its strokes would be misread anyway.
   */
  inkGroupOf?: (shape: TLShape) => string;
}

export interface BlackInkRaster {
  ctx: CanvasRenderingContext2D;
  /** Individual content shapes that failed even when rendered alone. */
  droppedContentShapes: TLShape[];
  /** Ink groups that could not be rendered and must not be labelled. */
  droppedInkKeys: string[];
}

/**
 * Composite `shapes` with all student handwriting black.
 *
 * Layers render transparent PNG and the paper is painted here: `background:
 * true` on an ink layer would arrive as an opaque sheet and blacken the page,
 * and JPEG has no alpha to stack with.
 *
 * Ink that fails to render is bisected down to the offending cluster and
 * reported in `droppedInkKeys`; the caller must not label those, since brackets
 * and ids come from the ink store rather than from the render.
 *
 * Holds no state between calls: two overlapping composites build their own
 * canvases and ask the editor for their own exports, and neither can see the
 * other's. Worth saying because an earlier version DID need serializing — it
 * suppressed typeset shapes by installing a `toSvg` on their util prototypes,
 * which is one object shared by the whole tab.
 *
 * @returns null only when nothing rendered at all.
 */
export async function rasterizeWithBlackInk(
  shapes: TLShape[],
  opts: BlackInkRasterOptions,
): Promise<BlackInkRaster | null> {
  const { bounds, scale, outputW, outputH, renderBatch, onBatchBoundary } =
    opts;
  const batchSize = opts.batchSize ?? Number.POSITIVE_INFINITY;

  const canvas = document.createElement("canvas");
  canvas.width = outputW;
  canvas.height = outputH;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.fillStyle = EXPORT_THEME.background;
  ctx.fillRect(0, 0, outputW, outputH);

  // Layers, in paint order: content, cached typeset, live typeset, ink.
  //
  // math and rich-text render through the foreignObject tldraw builds for a util
  // with no toSvg — the only thing that draws KaTeX, and ~278 ms of main thread
  // per shape. That is the cost the cache exists for, so when there is a cache
  // they come out of the content pass and into passes of their own.
  //
  // With NO cache they stay in `content` and are drawn in the board's own
  // order, exactly as before this layer existed. A pass of their own is only
  // worth its own full-frame export when there is a bitmap for it to sit above.
  //
  // Where the split does happen, the typeset passes land above content and
  // below ink. That is NOT the board's own order and is not claimed to be: an
  // annotation is tagged by `meta`, not by type, so an `annotate.*` custom-geo
  // is content here and now draws under the formula it was meant to ring. It
  // has not bitten — the agent rings handwriting, and annotations over ink are
  // dropped upstream — but it is an assumption, not a property.
  const content: TLShape[] = [];
  const liveTypeset: TLShape[] = [];
  const ink: TLShape[] = [];
  const isLayered = Boolean(opts.typesetLayer);
  const cachedIds = new Set(opts.typesetLayer?.shapes.map((shape) => shape.id));
  for (const shape of shapes) {
    if (cachedIds.has(shape.id)) continue;
    if (isStudentInk(shape)) ink.push(shape);
    // Parented typeset shapes stay in `content`: the export that draws their
    // ancestor draws them too, so a pass of their own would paint them twice.
    else if (
      isLayered &&
      ET_TYPESET_SHAPE_TYPES.has(shape.type) &&
      isTopLevelTypeset(shape)
    )
      liveTypeset.push(shape);
    else content.push(shape);
  }

  const renderAt = (batch: TLShape[], box: Box) =>
    renderBatch(batch, {
      format: "png",
      background: false,
      darkMode: false,
      bounds: box,
      padding: 0,
      pixelRatio: 1,
      scale,
    });
  const render = (batch: TLShape[]) => renderAt(batch, bounds);

  let drawn = 0;
  const draw = async (blob: Blob, blacken: boolean) => {
    const image = await decodeBlob(blob);
    try {
      // Coloured handwriting reads worse than black; missing reads not at all.
      ctx.drawImage(
        (blacken && paintBlack(image, outputW, outputH)) || image,
        0,
        0,
      );
    } finally {
      image.close();
    }
    drawn++;
  };

  // Content first; failed shapes are isolated without flattening survivors separately.
  //
  // Nothing suppresses anything here. tldraw renders the ids it is given plus
  // their descendants and nothing else (getSvgJsx), and StyleEmbedder only
  // walks that export's own SVG, so a shape the routing above kept out of this
  // batch costs this pass nothing at all.
  const contentBatches = chunk(content, batchSize);
  const droppedContentShapes: TLShape[] = [];
  for (const [i, batch] of contentBatches.entries()) {
    const recovered = await renderContentWithRetries(
      batch,
      render,
      onBatchBoundary,
    );
    if (recovered.value) await draw(recovered.value.blob, false);
    droppedContentShapes.push(...recovered.failed);
    if (onBatchBoundary && i < contentBatches.length - 1) {
      await onBatchBoundary();
    }
  }

  // The seeded worksheet, from one bitmap that outlives the press.
  let blittedLayer = false;
  const layer = opts.typesetLayer;
  if (layer) {
    const plan = layer.plan();
    let canvas: HTMLCanvasElement | null = null;
    // What the bitmap covers, which on a reuse is the rect it was built with
    // and not necessarily today's — a parent frame can move a formula's mask
    // without touching the formula's own record.
    let canvasBounds = layer.bounds;

    if (plan.kind === "reuse") {
      canvas = plan.canvas;
      canvasBounds = plan.bounds;
    } else {
      if (onBatchBoundary && contentBatches.length > 0) await onBatchBoundary();
      const toDraw = plan.kind === "patch" ? plan.redraw : layer.shapes;
      // A lone deletion draws nothing at all: the pixels come off the cached
      // bitmap and nothing has to be exported.
      const painted =
        toDraw.length === 0
          ? null
          : await renderTypesetLayer(
              toDraw,
              layer.bounds,
              scale,
              // Only the patch path keeps the rect; measuring it costs a
              // full-bitmap getImageData plus a per-pixel scan.
              plan.kind === "patch",
              renderAt,
              onBatchBoundary,
              droppedContentShapes,
              opts.renderTypeset,
            );

      if (plan.kind === "patch") {
        if (painted || toDraw.length === 0) {
          canvas = composePatch(plan, painted, layer.bounds, scale);
          // An incomplete repaint is shown once and redone on the next press.
          if (!painted || painted.is_complete) {
            layer.commit({
              bounds: layer.bounds,
              canvas,
              drawnShapes: toDraw,
              drawnRect: painted?.rect ?? null,
            });
          }
        } else {
          // The repaint failed. Apply the erase anyway and leave the entry
          // alone, so the next press tries the same patch again: showing the
          // cached bitmap untouched would put an equation the agent has already
          // deleted back on the picture, which is the one outcome the whole
          // erase design exists to avoid.
          canvas = composePatch(plan, null, layer.bounds, scale);
        }
      } else if (painted) {
        canvas = painted.canvas;
        // A rebuild draws every shape at once, so the alpha it reports is
        // their union and says nothing about any one of them. Recorded as
        // unmeasured, which is what it is: deleting one of these rebuilds.
        if (painted.is_complete) {
          layer.commit({
            bounds: layer.bounds,
            canvas,
            drawnShapes: layer.shapes,
            drawnRect: null,
          });
        }
      }
    }

    const blit =
      canvas &&
      typesetLayerBlit({
        source_bounds: canvasBounds,
        source_width_px: canvas.width,
        source_height_px: canvas.height,
        target_bounds: bounds,
        target_width_px: outputW,
        target_height_px: outputH,
      });
    if (canvas && blit) {
      ctx.drawImage(
        canvas,
        blit.sx,
        blit.sy,
        blit.sw,
        blit.sh,
        blit.dx,
        blit.dy,
        blit.dw,
        blit.dh,
      );
      drawn++;
      blittedLayer = true;
    }
  }

  // Annotation shapes, which the caller re-decides on every stroke and so
  // cannot be cached, plus everything left over when there is no cache.
  if (liveTypeset.length > 0) {
    if (onBatchBoundary && (contentBatches.length > 0 || blittedLayer)) {
      await onBatchBoundary();
    }
    const recovered = await renderContentWithRetries(
      liveTypeset,
      render,
      onBatchBoundary,
    );
    if (recovered.value) await draw(recovered.value.blob, false);
    droppedContentShapes.push(...recovered.failed);
  }

  // Handwriting on top, grouped so a failure drops whole clusters.
  const groups = groupInk(ink, opts.inkGroupOf);
  const groupBatches = chunkGroups(groups, batchSize);
  // Each loop skips its own trailing boundary, so without this the two
  // full-frame renders run back to back. Both phases must have work.
  if (
    onBatchBoundary &&
    (contentBatches.length > 0 || liveTypeset.length > 0 || blittedLayer) &&
    groupBatches.length > 0
  ) {
    await onBatchBoundary();
  }
  const droppedInkKeys: string[] = [];
  for (const [i, batch] of groupBatches.entries()) {
    const recovered = await renderWithRetries(
      batch,
      (groups) => render(groups.flatMap((group) => group.shapes)),
      onBatchBoundary,
    );
    for (const { value } of recovered.rendered) await draw(value.blob, true);
    droppedInkKeys.push(...recovered.failed.map((group) => group.key));
    if (onBatchBoundary && i < groupBatches.length - 1) {
      await onBatchBoundary();
    }
  }

  if (droppedInkKeys.length > 0) {
    console.error("[rasterize] Ink clusters failed to render", {
      droppedInkKeys,
    });
  }
  return drawn > 0 ? { ctx, droppedContentShapes, droppedInkKeys } : null;
}

/** A freshly drawn pass plus the page rect its pixels actually occupy. */
interface PaintedTypeset {
  canvas: HTMLCanvasElement;
  rect: Box | null;
  /** False when renderTypeset drew it without a face it needs. */
  is_complete: boolean;
}

/**
 * Draw typeset shapes over `bounds` onto a canvas of exactly that rect.
 *
 * Used for a rebuild (every shape) and for a patch (only the ones being
 * repainted); the bounds are the same either way, which is what lets the result
 * be stacked onto the cached bitmap without any offset arithmetic.
 *
 * Shapes that fail even alone are reported AND baked into the layer: a formula
 * that cannot render will not render on the next press either, and re-bisecting
 * it every time costs more than the missing glyphs do.
 *
 * Through `renderBatch` this is the pass that pays tldraw's foreignObject
 * export — the slow path. `renderTypeset`, when given, draws what it can
 * without it; whatever it hands back is exported and laid over the same
 * canvas. Either way the bitmap it returns saves the next press from paying
 * again.
 *
 * Input: `shouldMeasurePaint` — whether the caller will keep the painted rect.
 *   A rebuild will not, and measuring costs a full-bitmap `getImageData` plus a
 *   per-pixel scan.
 * @returns null when nothing rendered at all.
 */
async function renderTypesetLayer(
  shapes: TLShape[],
  bounds: Box,
  scale: number,
  shouldMeasurePaint: boolean,
  renderAt: (batch: TLShape[], box: Box) => ReturnType<RenderBatch>,
  onBatchBoundary: (() => Promise<void>) | undefined,
  droppedContentShapes: TLShape[],
  renderTypeset: RenderTypeset | undefined,
): Promise<PaintedTypeset | null> {
  // Sized from `bounds`, not from the image: every canvas in this layer has
  // to agree to the pixel, and the export's own size is the other convention.
  // They should be equal — tldraw floors the same product — and seating the
  // image at the origin costs nothing if they are.
  const { width_px, height_px } = typesetLayerCanvasSize(bounds, scale);

  let painted: ReturnType<typeof imageToCanvas> = null;
  let toExport = shapes;
  let is_complete = true;
  if (renderTypeset) {
    painted = imageToCanvas(null, width_px, height_px);
    if (!painted) return null;
    const result = await renderTypeset(shapes, bounds, painted.ctx);
    toExport = result.handed_back;
    is_complete = result.is_complete;
  }
  let has_drawn = toExport.length < shapes.length;

  // Whatever renderTypeset hands back is laid over what it drew, so such a
  // shape lands above typeset shapes it sat under. They are rare (media, or no
  // live DOM) and typeset shapes seldom overlap.
  if (toExport.length > 0) {
    const recovered = await renderContentWithRetries(
      toExport,
      (batch) => renderAt(batch, bounds),
      onBatchBoundary,
    );
    droppedContentShapes.push(...recovered.failed);
    if (recovered.value) {
      const image = await decodeBlob(recovered.value.blob);
      try {
        if (painted) painted.ctx.drawImage(image, 0, 0);
        else painted = imageToCanvas(image, width_px, height_px);
        has_drawn = true;
      } finally {
        image.close();
      }
    }
  }
  if (!painted || !has_drawn) return null;
  return {
    canvas: painted.canvas,
    rect: shouldMeasurePaint
      ? paintedPageRect(painted.ctx, bounds, scale)
      : null,
    is_complete,
  };
}

/**
 * The page rect covered by the non-transparent pixels of a freshly drawn pass.
 *
 * This is the whole reason a deleted formula can be erased at all. There is no
 * way to ask a shape how far KaTeX painted outside its box, and every attempt
 * to measure it from the DOM was wrong; the export's own alpha is not a
 * measurement of that question but the answer to it.
 *
 * @returns null when the pass drew nothing.
 */
function paintedPageRect(
  ctx: CanvasRenderingContext2D,
  bounds: Box,
  scale: number,
): Box | null {
  const { width, height } = ctx.canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  const pixels = new Uint32Array(data.buffer);

  // Rows top-down and bottom-up for the vertical extent; both are found on the
  // first painted row from each end, and a patch is mostly empty paper.
  let minY = -1;
  let minX = width;
  let maxX = -1;
  for (let y = 0; y < height; y++) {
    const span = paintedSpan(pixels, width, y, 0, width);
    if (!span) continue;
    minY = y;
    minX = span.first;
    maxX = span.last;
    break;
  }
  if (minY < 0) return null;
  let maxY = minY;
  for (let y = height - 1; y > minY; y--) {
    const span = paintedSpan(pixels, width, y, 0, width);
    if (!span) continue;
    maxY = y;
    if (span.first < minX) minX = span.first;
    if (span.last > maxX) maxX = span.last;
    break;
  }
  // The rows between can only WIDEN the band, so only the columns outside it
  // are worth looking at — which on a tall formula is nearly nothing.
  for (let y = minY + 1; y < maxY; y++) {
    if (minX > 0) {
      const left = paintedSpan(pixels, width, y, 0, minX);
      if (left) minX = left.first;
    }
    if (maxX < width - 1) {
      const right = paintedSpan(pixels, width, y, maxX + 1, width);
      if (right) maxX = right.last;
    }
  }

  return new Box(
    bounds.minX + minX / scale,
    bounds.minY + minY / scale,
    (maxX - minX + 1) / scale,
    (maxY - minY + 1) / scale,
  );
}

/**
 * Where the alpha byte sits in a `Uint32Array` view of RGBA bytes. Decided by
 * the machine, not by the format, so it is asked rather than assumed — reading
 * the wrong end would miss opaque black, which is most of what is being looked
 * for here.
 */
const ALPHA_MASK =
  new Uint8Array(new Uint32Array([1]).buffer)[0] === 1
    ? 0xff000000
    : 0x000000ff;

/** First and last painted x in row `y`, looking only at `[from, to)`. */
function paintedSpan(
  pixels: Uint32Array,
  width: number,
  y: number,
  from: number,
  to: number,
): { first: number; last: number } | null {
  const row = y * width;
  let first = -1;
  let last = -1;
  for (let x = from; x < to; x++) {
    if ((pixels[row + x] & ALPHA_MASK) === 0) continue;
    if (first < 0) first = x;
    last = x;
  }
  return first < 0 ? null : { first, last };
}

/**
 * Cached bitmap, minus what the deleted shapes covered, plus the repaint.
 *
 * A new canvas rather than a draw onto the cached one: a pass that fails
 * halfway must leave the entry exactly as it was. The seat offset is a whole
 * number of pixels by construction — both rects are snapped to the device grid
 * — so the accumulated bitmap is copied rather than resampled. Without that,
 * every patched press would soften everything already in the layer, and the
 * blur would compound all lesson.
 */
function composePatch(
  plan: Extract<TypesetLayerPlan, { kind: "patch" }>,
  painted: PaintedTypeset | null,
  bounds: Box,
  scale: number,
): HTMLCanvasElement {
  const canvas = painted?.canvas ?? document.createElement("canvas");
  if (!painted) {
    const { width_px, height_px } = typesetLayerCanvasSize(bounds, scale);
    canvas.width = width_px;
    canvas.height = height_px;
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;

  // The repaint is already on this canvas, so the cached bitmap goes behind it
  // and the erase has to bite only the cached half.
  const kept = document.createElement("canvas");
  kept.width = canvas.width;
  kept.height = canvas.height;
  const keptCtx = kept.getContext("2d");
  if (!keptCtx) return canvas;
  keptCtx.drawImage(
    plan.base,
    Math.round((plan.baseBounds.minX - bounds.minX) * scale),
    Math.round((plan.baseBounds.minY - bounds.minY) * scale),
  );
  if (plan.erase) {
    // Outward, so a rect that lands between pixels clears the whole of both.
    const left = Math.floor((plan.erase.minX - bounds.minX) * scale);
    const top = Math.floor((plan.erase.minY - bounds.minY) * scale);
    const right = Math.ceil((plan.erase.maxX - bounds.minX) * scale);
    const bottom = Math.ceil((plan.erase.maxY - bounds.minY) * scale);
    keptCtx.clearRect(left, top, right - left, bottom - top);
  }

  ctx.globalCompositeOperation = "destination-over";
  ctx.drawImage(kept, 0, 0);
  ctx.globalCompositeOperation = "source-over";
  return canvas;
}

/** One cluster's strokes, kept together so a failure drops all or none of it. */
interface InkGroup {
  key: string;
  shapes: TLShape[];
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size)
    out.push(items.slice(i, i + size));
  return out;
}

function groupInk(
  ink: TLShape[],
  keyOf?: (shape: TLShape) => string,
): InkGroup[] {
  const byKey = new Map<string, TLShape[]>();
  for (const shape of ink) {
    const key = keyOf?.(shape) ?? "";
    const group = byKey.get(key);
    if (group) group.push(shape);
    else byKey.set(key, [shape]);
  }
  return [...byKey].map(([key, shapes]) => ({ key, shapes }));
}

/** Whole clusters per batch, so bisection never splits one. */
function chunkGroups(groups: InkGroup[], size: number): InkGroup[][] {
  const out: InkGroup[][] = [];
  let batch: InkGroup[] = [];
  let count = 0;
  for (const group of groups) {
    if (batch.length > 0 && count + group.shapes.length > size) {
      out.push(batch);
      batch = [];
      count = 0;
    }
    batch.push(group);
    count += group.shapes.length;
  }
  if (batch.length > 0) out.push(batch);
  return out;
}

/**
 * Rasterize one cluster alone and enlarged — re-rendered from the vector path,
 * so it carries detail the page image never held, with nothing else in frame.
 */
export async function rasterizeInkCrop(
  strokes: TLShape[],
  opts: { bounds: Box; scale: number; renderBatch: RenderBatch },
): Promise<string | null> {
  if (strokes.length === 0) return null;
  const raster = await rasterizeWithBlackInk(strokes, {
    bounds: opts.bounds,
    scale: opts.scale,
    outputW: Math.max(1, Math.round(opts.bounds.w * opts.scale)),
    outputH: Math.max(1, Math.round(opts.bounds.h * opts.scale)),
    renderBatch: opts.renderBatch,
  });
  // One cluster per crop, so a drop means there is nothing to show.
  if (!raster || raster.droppedInkKeys.length > 0) return null;
  try {
    return await canvasToDataUrl(raster.ctx.canvas);
  } catch (e) {
    console.warn("[rasterizeInkCrop] encode failed", e);
    return null;
  }
}
